import { normalisePhone } from "./identifier.ts";

/**
 * The owner's phone contacts file (.vcf, vCard 2.1, 3.0 or 4.0), read in the browser. The file
 * never goes to Teamna: the page matches its numbers to the people of the group and sends only the
 * names of those people.
 */

export type ContactCard = { name: string; phones: string[] };

/** The digits that stay the same in local and international form: 0501234567 and +971501234567. */
const SUFFIX_DIGITS = 9;

/** Folded lines joined. A line that starts with a space or a tab continues the line before it. */
function unfold(text: string): string[] {
  return text.replace(/\r\n?/g, "\n").replace(/\n[ \t]/g, "").split("\n");
}

function decodeQuotedPrintable(value: string): string {
  const bytes: number[] = [];
  for (let i = 0; i < value.length; i += 1) {
    const char = value[i]!;
    const hex = value.slice(i + 1, i + 3);
    if (char === "=" && /^[0-9A-Fa-f]{2}$/.test(hex)) {
      bytes.push(Number.parseInt(hex, 16));
      i += 2;
    } else {
      bytes.push(...new TextEncoder().encode(char));
    }
  }
  return new TextDecoder("utf-8").decode(new Uint8Array(bytes));
}

function unescapeText(value: string): string {
  return value.replace(/\\([\\,;nN])/g, (_, char: string) => (char === "n" || char === "N" ? " " : char));
}

/** `N:Family;Given;Middle;Prefix;Suffix` as "Given Middle Family". */
function nameFromParts(value: string): string {
  const parts = value.split(/(?<!\\);/).map((part) => unescapeText(part).trim());
  const [family = "", given = "", middle = ""] = parts;
  return [given, middle, family].filter(Boolean).join(" ");
}

export function parseVcf(text: string): ContactCard[] {
  const lines = unfold(text);
  const cards: ContactCard[] = [];
  let card: { fn: string; n: string; phones: string[] } | null = null;

  for (let i = 0; i < lines.length; i += 1) {
    let line = lines[i]!;
    const colon = line.indexOf(":");
    if (colon < 0) continue;
    const head = line.slice(0, colon);
    // `item1.TEL;TYPE=CELL` is the property TEL with its parameters.
    const [property = "", ...params] = head.split(";");
    const name = property.slice(property.lastIndexOf(".") + 1).toUpperCase();
    const upperParams = params.map((param) => param.toUpperCase());
    const quotedPrintable = upperParams.includes("ENCODING=QUOTED-PRINTABLE") || upperParams.includes("QUOTED-PRINTABLE");
    // A quoted-printable value that ends with "=" continues on the next line (vCard 2.1 from Android).
    while (quotedPrintable && line.endsWith("=") && i + 1 < lines.length) {
      i += 1;
      line = line.slice(0, -1) + lines[i]!;
    }
    let value = line.slice(colon + 1);
    if (quotedPrintable) value = decodeQuotedPrintable(value);

    if (name === "BEGIN" && value.trim().toUpperCase() === "VCARD") {
      card = { fn: "", n: "", phones: [] };
    } else if (name === "END" && value.trim().toUpperCase() === "VCARD") {
      if (card) {
        const cardName = (card.fn || card.n).replace(/\s+/g, " ").trim();
        if (cardName && card.phones.length > 0) cards.push({ name: cardName, phones: card.phones });
      }
      card = null;
    } else if (card && name === "FN") {
      card.fn = unescapeText(value);
    } else if (card && name === "N") {
      card.n = nameFromParts(value);
    } else if (card && name === "TEL") {
      const phone = value.replace(/^tel:/i, "").trim();
      if (phone) card.phones.push(phone);
    }
  }
  return cards;
}

/** A name that says who the person is: three letters or more, and not a number. Teamna asks the same. */
function usableName(raw: string): string | null {
  const name = raw.trim().slice(0, 120).trim();
  if (normalisePhone(name)) return null;
  return (name.match(/\p{L}/gu)?.length ?? 0) >= 3 ? name : null;
}

function digitsOf(raw: string): string {
  return raw.replace(/\D/g, "");
}

/**
 * The name from the contacts file for each person, by id. A number in international form must be
 * the same number. A number in local form (0501234567, 01012345678) has no country, so it matches
 * on its last nine digits, and only when one person of the group has them.
 */
export function matchContactNames(
  cards: ContactCard[],
  people: Array<{ id: string; phoneE164: string }>,
): Map<string, string> {
  const byPhone = new Map(people.map((person) => [person.phoneE164, person.id]));
  const bySuffix = new Map<string, string[]>();
  for (const person of people) {
    const suffix = digitsOf(person.phoneE164).slice(-SUFFIX_DIGITS);
    bySuffix.set(suffix, [...(bySuffix.get(suffix) ?? []), person.id]);
  }

  const names = new Map<string, string>();
  for (const card of cards) {
    const name = usableName(card.name);
    if (!name) continue;
    for (const phone of card.phones) {
      const e164 = normalisePhone(phone);
      let id = e164 ? byPhone.get(e164) : undefined;
      const international = /^\s*(\+|00)/.test(phone);
      const digits = digitsOf(phone);
      if (!id && !international && digits.length >= SUFFIX_DIGITS) {
        const ids = bySuffix.get(digits.slice(-SUFFIX_DIGITS)) ?? [];
        if (ids.length === 1) id = ids[0];
      }
      // The first card with the number wins.
      if (id && !names.has(id)) names.set(id, name);
    }
  }
  return names;
}

/**
 * Words that say how a contact is related to a child, not who they are: "Kareem Mom", "Mama Layan",
 * "Um Kareem", "أبو تيم". An owner saves a parent like this when they do not know the name. Saved as
 * the parent's name, the parent reads it in the app later (#ai-feedback 2026-10-30), so a contacts
 * file never fills a name like this: the owner links the number with no name instead.
 */
const RELATION_WORDS = new Set([
  "mom",
  "mum",
  "mommy",
  "mummy",
  "mother",
  "mama",
  "mamma",
  "mami",
  "dad",
  "daddy",
  "father",
  "papa",
  "baba",
  "parent",
  "um",
  "umm",
  "om",
  "oum",
  "abu",
  "abou",
  "abo",
  "ام",
  "أم",
  "اُم",
  "ابو",
  "أبو",
  "والد",
  "والدة",
  "ماما",
  "بابا",
]);

/**
 * When a contact's name says a relation ("Kareem Mom", "Kareem's dad", "Um Kareem"), the other
 * words: the child's name, or null when only the relation is there ("Mom"). Null for a name that
 * is not a relation ("Kareem Haddad").
 */
export function relationContact(name: string): { child: string | null } | null {
  const words = name
    .normalize("NFC")
    .replace(/[’']s\b/giu, "")
    .split(/[\s,.()_-]+/u)
    .filter(Boolean);
  const relation = words.some((word) => RELATION_WORDS.has(word.toLowerCase()));
  if (!relation) return null;
  const rest = words.filter((word) => !RELATION_WORDS.has(word.toLowerCase()) && /\p{L}{2,}/u.test(word));
  return { child: rest.length ? rest.join(" ") : null };
}
